import { InactiveIcon } from '@components/std-components/InactiveIcon'
import ProductCardWrapper from '@components/std-components/ProductCard'
import { ProductOperation } from '@components/std-components/ProductOperation'
import { CentricTypography as Typography } from '@core/component/CentricTypography'
import { VIPChannel } from '@modules/common/models/enums/VIPChannel'
import { ProductCardPlaceholderImage } from '@resources/images'
import '@styles/ProductCard/style.css'
import classNames from 'classnames'
import React, { FC, useMemo } from 'react'
import { ProductCardSizes } from '@constants'
import * as css from './ProductCard.css'
import { ProductTitle } from './ProductTitle'

export enum FieldType {
  VALUE = 'value',
  LABEL_AND_VALUE = 'label&value',
  LABEL = 'label',
  DIVIDER = 'divider',
  ELEMENT = 'element'
}

export interface FieldConfig {
  id: string
  type?: FieldType
  label?: string
  value?: string
  element?: React.ElementType
  allowedForChannels: VIPChannel[]
}

export const FieldValueRenderer = ({ value }) => {
  return value ? (
    <div className={`product-card-info-text`}>
      <Typography variant={'captionDarkGrey'} noWrap align={'center'}>
        {value}
      </Typography>
    </div>
  ) : null
}

export const FieldLabelValueRenderer = ({ label, value }) => (
  <div className='flex column'>
    {label && (
      <div className={`product-card-info-field-label`}>
        <Typography variant={'overlineLightGrey'}>{label.toUpperCase()}</Typography>
      </div>
    )}
    <div className={`product-card-info-field-text`}>
      <Typography variant={'h6'}>{value}</Typography>
    </div>
  </div>
)

export const Divider = () => <div className='product-card-info-divider-faded' />

const FieldTypeElementMap = {
  [FieldType.VALUE]: FieldValueRenderer,
  [FieldType.LABEL_AND_VALUE]: FieldLabelValueRenderer,
  [FieldType.DIVIDER]: Divider
}

export interface ProductCardProps {
  title: string
  fields: FieldConfig[]
  imageUrl?: string
  size: ProductCardSizes
  topRightIcon?: React.ReactElement
  topLeftIcon?: React.ReactElement
  bottomRightIcon?: React.ReactElement
  isActive: boolean
  onClick: any
  productClasses?: string
  titleRenderer?: React.ElementType
  cardContainerCss?: string
  cardCss?: string
  disabled?: boolean
  showRecommendedIcon?: boolean
  showCoreIcon?: boolean
}

export const ProductCard: FC<ProductCardProps> = ({
  title,
  fields,
  imageUrl,
  size,
  topRightIcon,
  topLeftIcon,
  bottomRightIcon,
  onClick,
  isActive,
  productClasses,
  titleRenderer = ProductTitle,
  cardContainerCss = '',
  cardCss = '',
  disabled,
  showRecommendedIcon,
  showCoreIcon
}) => {
  const TitleCmp = titleRenderer
  const mediaUrlObj = useMemo(
    () =>
      imageUrl
        ? { src: imageUrl, alt: title }
        : { src: ProductCardPlaceholderImage, alt: title },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [imageUrl]
  )
  return (
    <div
      className={classNames(css.productCard, cardContainerCss, {
        'card-disabled': disabled
      })}
    >
      <ProductCardWrapper
        size={size ? size : ProductCardSizes.Medium}
        isActive={isActive}
        cardCss={cardCss}
      >
        <ProductOperation
          mediaVariant={'card'}
          media={mediaUrlObj}
          bottomRightIcon={bottomRightIcon}
          onClick={onClick}
          topRightIcon={topRightIcon}
          topLeftIcon={topLeftIcon || (!isActive && <InactiveIcon />)}
          className={productClasses}
        />
        <div className={`product-card-info `}>
          <TitleCmp title={title} />
          {fields.map(field => {
            const FieldCmp = FieldTypeElementMap[field.type] || field.element

            return FieldCmp ? <FieldCmp key={field.id} {...field} /> : null
          })}
        </div>
      </ProductCardWrapper>
    </div>
  )
}
